import { DecimalPipe } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AlertController,
  LoadingController,
  ModalController,
  NavParams,
  Platform,
  ToastController,
} from "ionic-angular";
import moment from "moment";
import { Subscription } from "rxjs";
import { Observable } from "rxjs/Observable";
import { takeUntil } from "rxjs/operators";
import { AccessControlService } from "../../../../app/access-control/access-control-service";
import { VISIT_PAGE_CARE_PLAN } from "../../../../app/access-control/common-permission-asks";
import { CarePlanVisit } from "../../../../app/access-control/permissions";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import ContentWrapper from "../../../../models/content-wrapper/content-wrapper";
import { BasePage } from "../../../../pages/base-page";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { ConfigurationService } from "../../../../providers/configuration/configuration.service";
import { OrderDataService } from "../../../../providers/order-data-service/order-data-service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";

const dateTimeFormat = "MM/DD/YYYY HH:mm";
const dateFormat = "MM/DD/YYYY";

@Component({
  selector: "visit-frequency-list-card",
  templateUrl: "visit-frequency-list-card.html",
})
export class VisitFrequencyListCard
  extends BasePage
  implements OnInit, OnChanges
{
  @Input() patChartId: string;
  @Input() config; // This config is a static file and can probably be imported into this component unless there's a good reason _not_ to?
  @Input() disabled: boolean = false;

  id: string;
  componentId: string;
  label: string;
  tooltip: object;
  colHeaders: string[];
  conditionalFormatting: object;
  deltaFields: string[];
  rawData: any[] = [];
  dataRows: any[] = [];
  dummyRow: object;
  prevRow: {};
  declinable: boolean;
  editable: boolean = false;
  manualAdd: boolean = true;
  seeMore: boolean = true;
  seeMoreText: boolean = true;
  seeMoreLength: number = 0;
  dataUrl: string;
  allowExternalData: boolean = false;
  configLoaded: boolean = false;
  delta: {};
  enums = CarePlanVisit;
  dataPages: Array<object[]> = [];
  pageSize: number = 5;
  currentPage: number = 0;
  visitFreqTotalCount: number = 0;
  patientId: string;
  tabHeaders: Set<String>;
  visitFrequencyListItemInitialData: ContentWrapper<Object>;
  meetingStatus: string;
  isNewCal: boolean = false;
  OEToggle: boolean = false;
  showIdgTempChangesToggle: boolean = false;
  permissionsAccess = {} as PermissionsResponse;
  private spinnerCounter: number = 0;
  private spinner: any = null;
  private _segment = "";
  private showOESubscription: Subscription;

  static DECLINED_TYPE_LOOKUP_CODE = "CL015.1";
  static DISCONTINUED_TYPE_LOOKUP_CODE = "CL015.2";

  constructor(
    public modalCtrl: ModalController,
    public navParams: NavParams,
    public dataService: AssessmentDataService,
    public decimalPipe: DecimalPipe,
    public plt: Platform,
    private alertCtrl: AlertController,
    public translatePipe: TranslateService,
    private http: HttpClient,
    private accessCntrl: AccessControlService,
    private configurationService: ConfigurationService,
    private orderService: OrderDataService,
    public stateData: StateManagementService,
    public toastCtrl: ToastController,
    private loadingCtrl: LoadingController
  ) {
    super(stateData);
    this.dataUrl = this.configurationService.getConfig().apiUrl;
    this.accessCntrl.getPermission(VISIT_PAGE_CARE_PLAN).subscribe((data) => {
      this.permissionsAccess = data;
    });
  }

  ngOnInit(): void {
    // SUP-19982: released by logout$ before resetState() broadcasts the null
    // reset, which otherwise re-issued this call once per surviving instance.
    this.showOESubscription = this.stateData.showOrderEntryOM
      .pipe(takeUntil(this.stateData.logout$))
      .subscribe((x) => {
        if (x) {
          this.OEToggle = x;
        }
        this.loadOrRefresh();
      });
  }

  getAccess(resource: string) {
    return this.accessCntrl.getAccessLevel(this.permissionsAccess, resource);
  }

  set segment(value) {
    this._segment = value;
    this.currentPage = 0;
    this.updateVisitFrequencyData();
  }

  get segment() {
    return this._segment;
  }

  // Listen to the patChartId change in for use in IDG list..
  // Before, the IDG page would fetch the initial list of VFs, but this component does that itself.
  // So now we listen to the patient chart id change
  ngOnChanges(changes: SimpleChanges) {
    this.loadOrRefresh();
  }

  loadOrRefresh() {
    this.tabHeaders = new Set(["activeOrders"]);
    if (this.segment !== "activeOrders") {
      this.segment = "activeOrders";
    }
    this.loadConfig();

    this.firstPage();
    this.updateVisitFrequencyData();
    this.checkForDeclinedVisitFrequencies();
    this.findPreMigrationVisitFrequenciesFromOldOM();
  }

  checkForDeclinedVisitFrequencies() {
    let types = [VisitFrequencyListCard.DECLINED_TYPE_LOOKUP_CODE];
    if (!this.OEToggle) {
      types.push(VisitFrequencyListCard.DISCONTINUED_TYPE_LOOKUP_CODE);
    }
    this.getVisitFrequencyData(types).subscribe(
      (data) => {
        const count = data["x_total_count"];
        if (count) {
          this.tabHeaders = this.tabHeaders.add("declinedOrders");
        }
      },
      (err) => {
        console.error(err);
        this.toastCtrl
          .create({
            message: "Error loading visit frequencies",
            duration: 6000,
            position: "below",
            cssClass: "error",
          })
          .present();
      }
    );
  }

  findPreMigrationVisitFrequenciesFromOldOM() {
    this.orderService.patientInfoFromQBOSId(this.patChartId).subscribe(
      (data) => {
        this.patientId = data["id"];
        this.orderService
          .getVisitFrequencyActiveOrders(this.patientId, 5, 0)
          .subscribe(
            (data) => {
              this.visitFrequencyListItemInitialData = data;
              if (data.content && data.content.length) {
                this.tabHeaders = this.tabHeaders.add("others");
              }
            },
            (err) => {
              console.error(err);
            }
          );
      },
      (err) => {
        console.error(err);
      }
    );
  }

  loadConfig() {
    if (!this.configLoaded) {
      this.id = this.config.id;
      this.componentId = this.config.componentId;
      this.label =
        this.config.label !== undefined ? this.config.label : this.config.id;
      this.config.cardName = "visitFrequency";
      this.tooltip = this.config.tooltip;
      this.colHeaders = this.config.data.listHeaders;
      this.conditionalFormatting = this.config.data.conditionalFormatting;
      this.deltaFields = this.config.data.deltaFields || [];
      this.declinable = this.config.declinable;
      this.meetingStatus = this.config.meetingStatus;
      if (this.config.seeMore) {
        this.seeMore = !this.config.seeMore;
        this.seeMoreLength = this.config.seeMoreLength;
      }
      if (this.config.editable) {
        this.editable = this.config.editable;
      }
      if (
        this.config.manualAdd !== undefined &&
        this.config.manualAdd !== null
      ) {
        this.manualAdd = this.config.manualAdd;
      }
      this.dummyRow = { id: "" };
      let dummyData = {};
      for (let header of this.colHeaders) {
        dummyData[header] = "-";
      }
      this.dummyRow["values"] = dummyData;

      this.configLoaded = true;
    }
  }

  ingestData(incomingData) {
    // Call some service to load rows and prevRow
    this.dataRows = [];
    if (incomingData.data) {
      this.rawData = incomingData.data;
      for (let data of incomingData.data) {
        let info = {
          id: data.id,
          seemore: this.seeMore,
          seemoretext: this.seeMoreText,
        };
        let values = this.parseDataRow(data);
        info["values"] = values;
        this.dataRows.push(info);
      }
    }
    if (incomingData.total) {
      this.visitFreqTotalCount = incomingData.total;
    }

    this.updateDataPages();

    if (incomingData.previous) {
      let prevData = incomingData.previous;
      let prevInfo = this.parseDataRow(prevData);
      this.prevRow = prevInfo;
    }
    this.updateDelta();
  }

  presentListForm(): void {
    let disciplines = [];
    for (let i = 0; i < this.dataRows.length; i++) {
      disciplines.push(this.dataRows[i].values.roleLookupCode);
    }

    let newObj = JSON.parse(JSON.stringify(this.config));
    // let newObj = Object.assign({}, this.config);
    newObj.inputs.discipline.options = newObj.inputs.discipline.options.filter(
      (data) => disciplines.indexOf(data.value) < 0
    );

    let listModal = this.modalCtrl.create(
      "InputModalPage",
      {
        config: newObj,
      },
      { enableBackdropDismiss: false }
    );
    listModal.onDidDismiss((data) => {
      // console.log( "GOT DATA BACK!:" );
      // console.log(data);

      if (data !== undefined) {
        this.addData(data);
      }
    });
    listModal.present();
  }

  getVisitFrequencyData(typeLookupCodes?: string[]): Observable<any> {
    return new Observable((observer) => {
      let url = `${this.dataUrl}/patients-charts/${
        this.patChartId
      }/visitfrequencies?$limit=${this.pageSize}&$offset=${
        this.currentPage * this.pageSize
      }`;
      if (typeLookupCodes) {
        url += "&typeLookupCodes=" + typeLookupCodes.toString();
      }
      this.increaseSpinnerCount();
      this.http.get(url).subscribe(
        (response) => {
          this.visitFreqTotalCount = response["x_total_count"];
          observer.next(response);
          observer.complete();
          this.decreaseSpinnerCount();
        },
        (err) => {
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  updateVisitFrequencyData() {
    let types = [];
    if (this.segment === "declinedOrders") {
      types.push(VisitFrequencyListCard.DECLINED_TYPE_LOOKUP_CODE);
      if (!this.OEToggle) {
        types.push(VisitFrequencyListCard.DISCONTINUED_TYPE_LOOKUP_CODE);
      }
    }
    this.getVisitFrequencyData(types).subscribe(
      (data) => {
        this.ingestData({ data: data.visitFrequencies });
      },
      (err) => {
        console.error(err);
        this.ingestData({ data: [] });
        this.toastCtrl
          .create({
            message: "Error loading visit frequencies",
            duration: 6000,
            position: "below",
            cssClass: "error",
          })
          .present();
      }
    );
  }

  updateDelta() {
    if (this.prevRow) {
      let deltaFields = this.deltaFields;
      let dataRows = this.dataRows;
      let prevRow = this.prevRow;
      this.delta = {};
      for (let header of this.colHeaders) {
        let diff: any = "-";

        if (deltaFields.indexOf(header) == -1) {
          this.delta[header] = diff;
          continue;
        }

        if (header == "date" && moment(dataRows[0].values[header])) {
          // console.log(header);
          let end = moment(dataRows[0].values[header]);
          let start = moment(prevRow[header]);
          let duration = moment.duration(start.diff(end));
          diff = duration.humanize() + " ago";
        } else if (!isNaN(dataRows[0].values[header])) {
          diff = dataRows[0].values[header] - prevRow[header];
          diff = parseFloat(this.decimalPipe.transform(diff, "1.0-2"));
          if (diff > 0) {
            diff = "+" + diff;
          }
        }
        this.delta[header] = diff;
      }
    }
  }

  flattenIncomingData(data: object): object {
    let flatData = {};
    for (let key of Object.keys(data)) {
      if (typeof data[key] == "object") {
        let innerObj = data[key];
        if (key != "reason" && innerObj == undefined) {
          continue;
        }

        // console.log( "Detected incoming object to parse: ", key, innerObj );

        if (
          key !== "reportedOn" &&
          key !== "frequency" &&
          key !== "bereavementCharacteristics" &&
          key !== "reasonLookup" &&
          key !== "reasonLookupCode" &&
          key != "reason"
        ) {
          for (let innerKey of Object.keys(innerObj)) {
            let newKey =
              key + innerKey.replace(/\b\w/g, (l) => l.toUpperCase());
            flatData[newKey] = innerObj[innerKey];
          }
        } else if (key == "reportedOn") {
          flatData[key] = innerObj["date"] + "T" + innerObj["time"];
        } else if (key == "frequency" && innerObj.timeUnit !== undefined) {
          // console.log( "Detected frquency input, parsing to string: ", innerObj );
          flatData[key] =
            innerObj.numerator +
            this.translatePipe.instant(" time(s) per ") +
            innerObj.denominator +
            " " +
            this.translatePipe.instant(innerObj.timeUnit);
        } else if (key === "bereavementCharacteristics") {
          let arr = data[key].map((item) => this.translatePipe.instant(item));
          flatData[key] = arr.join(", ");
        } else if (key === "reasonLookupCode") {
          let arr = data[key];
          flatData[key] = arr;
        } else if (key === "reasonLookup") {
          let arr = data[key];
          flatData[key] = arr;
        } else if (key === "reason") {
          flatData[key] = data[key];
        }
      } else {
        flatData[key] = data[key];
      }
    }
    // console.log( "FLATTENED IT: ", flatData );
    return flatData;
  }

  parseDataRow(data: object) {
    let values = data; // {};
    let flatData = this.flattenIncomingData(data);
    //   console.log(this);
    //   console.log(`Col headers: ${this.colHeaders}`)
    for (let header of this.colHeaders) {
      // console.log( "Checking header: ", header );
      let val = "-";
      if (flatData[header]) {
        if (header === "date" || header === "reportedOn") {
          flatData[header] = moment(flatData[header]).format(dateTimeFormat);
        }
        val = flatData[header];
      }
      if (header === "discipline") {
        val =
          data["roleLookUpValue"].length > 0
            ? data["roleLookUpValue"]
            : data["roleLookupCode"];
      }
      if (header === "visits") {
        val = data["visitCountLookUpValue"];
      }
      if (header === "timeInterval") {
        val = data["visitIntervalLookUpValue"];
      }
      if (header === "prnVisits") {
        val = data["visitPrnLookUpValue"];
      }
      if (header === "reason") {
        if (data["reason"]) {
          val = data["reason"];
        } else {
          let newVal = "";
          if (
            data["reasonLookupCode"] != null &&
            data["reasonLookupCode"] != undefined
          ) {
            if (data["reasonLookupCode"].indexOf("CL014.1") > -1) {
              newVal += data["reasonLookup"]["CL014.1"] + ",";
            }

            if (data["reasonLookupCode"].indexOf("CL014.2") > -1) {
              newVal += data["reasonLookup"]["CL014.2"] + ",";
            }
          }

          val = newVal != "" ? newVal.substr(0, newVal.length - 1) : "";
        }
      }
      if (header === "startDate") {
        val =
          data["startedDte"] != null
            ? moment(data["startedDte"].toString()).format(dateFormat)
            : "";
      }
      if (header === "endDate") {
        val =
          data["completedDte"] != null
            ? moment(data["completedDte"].toString()).format(dateFormat)
            : "";
      }
      // console.log( "Produced val: ", val );
      values[header] = val;
    }
    return values;
  }

  addData(data: any): void {
    // console.log( "Data being pushed:" , data );
    if (this.currentPage > 0) {
      this.firstPage();
    }

    if (data.reasonValue != null && data.reasonValue != undefined) {
      if (data.reason != null && data.reason != undefined) {
        data.reason.push("CL014.3");
      } else {
        data.reason = ["CL014.3"];
      }
    }

    if (data) {
      let addObject = {
        patChartId: this.patChartId,
        roleLookupCode: data.discipline,
        visitCountLookupCode: data.visits,
        visitIntervalLookupCode: data.timeInterval,
        visitPrnLookupCode: data.prnVisits,
        reasonLookupCode: data.reason,
        startedDte: data.startDate.date,
        completedDte: data.endDate.date,
        reason: data.reasonValue,
      };
      // console.log("Adding: ", addObject);
      try {
        this.http
          .post(this.dataUrl + "/visitfrequencies/", addObject)
          .subscribe(
            (data) => {
              this.updateVisitFrequencyData();
            },
            (err) => {
              console.log(err);
            }
          );
      } catch (error) {
        // console.log( "Error during .", error );
      }
    }
  }

  editRow(row: object): void {
    let modalConfig = { config: this.config };

    let values = this.rawData.filter((record) => record.id == row["id"])[0];
    if (values !== null || values !== undefined) {
      let inputKeys = Object.keys(this.config.inputs);
      let editData = {};

      for (let key of inputKeys) {
        if (values[key]) {
          editData[key] = values[key];
        } else {
          editData[key] = null;
        }
      }

      var reasonOther = [];
      if (
        values.reasonLookupCode != null &&
        values.reasonLookupCode != undefined
      ) {
        var index = values.reasonLookupCode.indexOf("CL014.3");
        if (index > -1) {
          // values.reasonLookupCode.splice(index, 1);
          reasonOther = ["other"];
        }
      }

      editData["discipline"] = values.roleLookupCode;
      editData["visits"] = values.visitCountLookupCode;
      editData["timeInterval"] = values.visitIntervalLookupCode;
      editData["prnVisits"] = values.visitPrnLookupCode;
      editData["reason"] = values.reasonLookupCode;
      editData["reasonOther"] = reasonOther;
      editData["reasonValue"] =
        values.reasonLookupCode != null
          ? values.reasonLookupCode.indexOf("CL014.3") > -1
            ? values.reason
            : ""
          : "";
      editData["startDate"] = { date: values.startedDte };
      editData["endDate"] = { date: values.completedDte };
      modalConfig["data"] = editData;
    }

    // console.log( "Launching edit with modal config: ", modalConfig );
    let listModal = this.modalCtrl.create("InputModalPage", modalConfig, {
      enableBackdropDismiss: false,
    });
    listModal.onDidDismiss((data) => {
      // console.log( "GOT DATA BACK!:" );
      // console.log(data);

      if (data !== undefined) {
        this.editData(row, row["id"], data);
      }
    });
    listModal.present();
  }

  editData(oldRow: object, rowId: string, data: any): void {
    if (data.reasonValue != null && data.reasonValue != undefined) {
      if (data.reason != null && data.reason != undefined) {
        data.reason.push("CL014.3");
      } else {
        data.reason = ["CL014.3"];
      }
    }

    if (data) {
      try {
        let editObject = {
          id: rowId,
          patChartId: this.patChartId,
          roleLookupCode: data.discipline,
          visitCountLookupCode: data.visits,
          visitIntervalLookupCode: data.timeInterval,
          visitPrnLookupCode: data.prnVisits,
          reasonLookupCode: data.reason,
          startedDte: data.startDate.date,
          completedDte: data.endDate.date,
          reason: data.reasonValue != undefined ? data.reasonValue : "",
        };
        // console.log("Editing: ", editObject);
        this.http
          .put(this.dataUrl + "/visitfrequencies/" + rowId, editObject)
          .subscribe(
            (data) => {
              // this.updateVisitFrequencyData();
              this.changePage();
            },
            (err) => {
              console.error(err);
              this.toastCtrl
                .create({
                  message: "Error editing visit frequencies",
                  duration: 6000,
                  position: "below",
                  cssClass: "error",
                })
                .present();
            }
          );
      } catch (error) {
        //console.log( "Error during data submission and building.", error );
      }
    }
  }

  deleteRow(id: string) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant("confirmDeleteRecord"),
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            // console.log("Deleting: ", id);
            try {
              this.http
                .delete(this.dataUrl + "/visitfrequencies/" + id)
                .subscribe(
                  (data) => {
                    this.changePage();
                  },
                  (err) => {
                    console.error(err);
                    this.toastCtrl
                      .create({
                        message: "Error deleting visit frequencies",
                        duration: 6000,
                        position: "below",
                        cssClass: "error",
                      })
                      .present();
                  }
                );
            } catch (error) {
              // console.log( "Error during data submission and building.", error );
            }
          },
        },
      ],
    });
    alert.present();
  }

  showRowHistory(id: string) {
    let detailsModal = this.modalCtrl.create(
      "VisitFrequencyHistoryModalPage",
      { id: id },
      { enableBackdropDismiss: false }
    );
    detailsModal.onDidDismiss((data) => {});
    detailsModal.present();
  }

  updateDataPages() {
    this.dataPages = [];

    let dataRows = this.dataRows;
    let pageSize = this.pageSize;
    let dataPages = this.dataPages;
    let pageIndex = -1;
    for (let i = 0; i < this.visitFreqTotalCount; i++) {
      let j = i % pageSize;
      if (j == 0) {
        dataPages.push([]);
        pageIndex++;
      }
      dataPages[pageIndex][j] = dataRows[i] || this.dummyRow;
    }
    if (pageIndex > 0) {
      let endingRow = dataRows.length % pageSize;
      if (endingRow > 0) {
        for (let i = endingRow; i < pageSize; i++) {
          dataPages[pageIndex][i] = this.dummyRow;
        }
      }
    }

    if (this.currentPage > dataRows.length % pageSize) {
      this.prevPage();
    }
    // console.log( "Produced pages: ", this.dataPages );
  }

  populatePage(response) {
    if (this.dataPages.length > 1) {
      while (this.dataPages[this.currentPage].length < this.pageSize) {
        this.dataPages[this.currentPage].push(this.dummyRow);
      }
    }

    for (let i in this.dataPages[this.currentPage]) {
      this.dataPages[this.currentPage][i] = this.dummyRow;
    }

    for (let index in response.visitFrequencies) {
      let data = response.visitFrequencies[index];
      let info = {
        id: data.id,
        seemore: this.seeMore,
        seemoretext: this.seeMoreText,
      };
      let values = this.parseDataRow(data);
      info["values"] = values;
      this.dataPages[this.currentPage][index] = info;
    }

    if (response.visitFrequencies.length == 0) {
      this.updateDataPages();
    }
    // if we want to maintain pageSize rows
    // while (this.dataPages[this.currentPage].length < this.pageSize) {
    //   this.dataPages[this.currentPage].push(this.dummyRow);
    // }
  }

  firstPage() {
    if (this.currentPage !== 0) {
      this.currentPage = 0;
      this.changePage();
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.changePage();
    }
  }

  setPage(page) {
    if (
      page >= 0 &&
      page <= this.dataPages.length &&
      page !== this.currentPage
    ) {
      this.currentPage = page;
      this.changePage();
    }
  }

  nextPage() {
    if (this.currentPage < this.dataPages.length - 1) {
      this.currentPage++;
      this.changePage();
    }
  }

  lastPage() {
    if (this.currentPage !== this.dataPages.length - 1) {
      this.currentPage = this.dataPages.length - 1;
      this.changePage();
    }
  }

  changePage() {
    let types = [];
    if (this.segment === "declinedOrders") {
      types.push(VisitFrequencyListCard.DECLINED_TYPE_LOOKUP_CODE);
      if (!this.OEToggle) {
        types.push(VisitFrequencyListCard.DISCONTINUED_TYPE_LOOKUP_CODE);
      }
    }
    this.getVisitFrequencyData(types).subscribe(
      (response) => {
        this.populatePage(response);
        this.rawData = response.visitFrequencies;
      },
      (err) => {
        this.ingestData({ data: [] });
        console.error(err);
        this.toastCtrl
          .create({
            message: "Error loading visit frequencies",
            duration: 6000,
            position: "below",
            cssClass: "error",
          })
          .present();
      }
    );
  }

  increaseSpinnerCount(message: string = "Loading visit frequencies") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  ngOnDestroy() {
    if (this.showOESubscription) {
      this.showOESubscription.unsubscribe();
    }
  }

  ShowMore(row: object): void {
    let values = this.rawData.filter((record) => record.id == row["id"])[0];
    if (values !== null || values !== undefined) {
      row["seemore"] = true;
      row["seemoretext"] = false;
      this.updateDataPages();
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/visit-frequency-list-card/visit-frequency-list-card.ts